import jira from 'src/lib/api/jira';

const anyJiraObjectToOption = (o) => {
  if (typeof o === 'string') {
    return [{
      label: o,
      id: o
    }];
  }

  const label = o.displayName || o.name || o.label || o.summaryText || o.summary || o.value;
  const icon = o.iconUrl || o.img || Object.values(o.avatarUrls || {})[0];
  const name = o.name;
  const key = o.key;
  const id = o.id;

  const result = [{ label, icon, name, key, id }];

  if (Array.isArray(o.children)) {
    o.children.forEach(c => {
      const [child] = anyJiraObjectToOption(c);
      result.push({
        label: `${label} - ${child.label}`,
        icon,
        name,
        key,
        id,
        child
      });
    });
  }

  return result;
};

const autocompleteSearchResultToArray = (rawData) => {
  if (Array.isArray(rawData)) return rawData;
  if (!rawData) return [];

  if (Array.isArray(rawData.sections)) {
    return rawData
      .sections
      .flatMap(section => {
        return Object
          .values(section)
          .flatMap(
            sectionField => (Array.isArray(sectionField) ? sectionField : [])
          );
      });
  }

  console.error(`unknown response from Jira:`, rawData);
  throw new Error('Unknown response format for Jira AutocompleteSearchResult');
};

export const createAllowedValuesSource = (allowedValues) => {
  const options = allowedValues.flatMap(anyJiraObjectToOption);
  return {
    label: 'Select',
    loadOptions: async filter => options,
  };
};

export const createJiraUserSource = () => {
  return {
    label: 'Select',
    loadOptions: async filter => {
      const res = await jira.getUsers(filter);
      return (res || []).flatMap(anyJiraObjectToOption);
    },
  };
};

export const createJiraGroupSource = () => {
  return {
    label: 'Select',
    loadOptions: async filter => {
      const res = await jira.getGroups(filter);
      return (res || [])
        .map(group => ({ ...group, key: group.name, img: 'group' }))
        .flatMap(anyJiraObjectToOption);
    },
  };
};

export const createAutoCompleteUrlSource = (autoCompleteUrl, projectKeys) => {
  const url = autoCompleteUrl.replace('issueKey=null', `project=${projectKeys}`);

  return {
    label: 'Select',
    loadOptions: async filter => {
      const res = await jira.searchByAutocompleteUrl(url, filter);
      return autocompleteSearchResultToArray(res).flatMap(anyJiraObjectToOption);
    },
  };
};

const readableFieldToOption = (f) => {
  return {
    label: f.displayName || f.name || f.label || f.value,
    icon: 'jira',
    id: f.apiName,
  };
};

export const createJiraFieldsStringSource = (fields) => {
  const options = fields
    .filter(field => {
      const { schema: { type, items } = {} } = field || {};
      const itemType = items || type;
      return [
        'string', 'number', 'project', 'user', 'option', 'date', 'issuetype'
      ].includes(itemType);
    })
    .map(readableFieldToOption)
    .filter(o => Boolean(o));

  return {
    label: 'Issue field',
    loadOptions: async filter => options
  };
};

export const createJiraFieldsUserSource = (fields) => {
  const options = fields
    .filter(f => f && f.schema && (f.schema.type === 'user' || f.schema.items === 'user'))
    .map(readableFieldToOption)
    .filter(o => Boolean(o));

  return {
    label: 'Issue field',
    loadOptions: async filter => options
  };
};

export const createCustomerOrganizationsSource = () => {
  const options =
    jira
      .loadAllCustomerOrganizations()
      .then(orgs => orgs.flatMap(anyJiraObjectToOption).map(o => ({ ...o, icon: 'org' })));

  // suppress unhandled rejection warning. The error if any is handled by UI
  options.catch(() => {});

  return {
    label: 'Select',
    loadOptions: async filter => options,
  };
};
